import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper, Chip, TextField, Autocomplete, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import { useSearchParams } from 'react-router-dom';
import { listMacieBuckets } from '../../../services/macieApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx, statusChipSx,
} from '../portalStyles';

const MACIE_COLOR = '#0d9488';

const PUBLIC_OPTIONS = ['PUBLIC', 'NOT_PUBLIC', 'UNKNOWN'];
const SHARED_OPTIONS = ['EXTERNAL', 'INTERNAL', 'NOT_SHARED', 'UNKNOWN'];
// macie2 serverSideEncryption.type values.
const ENCRYPTION_OPTIONS = ['NONE', 'AES256', 'aws:kms', 'aws:kms:dsse'];

// human-readable byte size (1024-based).
const formatBytes = (n) => {
  const bytes = Number(n) || 0;
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const val = bytes / (1024 ** i);
  return `${val >= 100 || i === 0 ? Math.round(val) : val.toFixed(1)} ${units[i]}`;
};

// An account with no name renders its id ONCE.
//
// The sibling pages (Enrollment, Security Hub's EnrollmentPage) all format an
// account as `Name (id)` with the BARE id as the fallback. This page instead
// printed `accountLabel(id) (id)` while accountLabel itself fell back to the id, so
// an unnamed account came out as "123456789012 (123456789012)". The `name !== id`
// guard keeps that true even if the account list starts echoing ids as names.
const accountText = (name, id) => (name && name !== id ? `${name} (${id})` : id);

const publicIntent = (v) => (v === 'PUBLIC' ? 'error' : v === 'NOT_PUBLIC' ? 'success' : 'neutral');
const sharedIntent = (v) => (v === 'EXTERNAL' ? 'warning' : v === 'INTERNAL' ? 'info' : 'neutral');

// Macie's "could not tell" for the default encryption type. mapBucketMetadata
// defaults an absent serverSideEncryption.type to the literal 'UNKNOWN'; a row
// cached before the field was collected carries '' instead. Both mean unmeasured.
const isEncUnknown = (v) => !v || String(v).toUpperCase() === 'UNKNOWN';

// UNKNOWN is its own state in its own NEUTRAL colour, for exactly the reason
// UPLOAD_POLICY_UNKNOWN below is: "Macie could not tell" is not "this bucket is
// encrypted". This used to colour anything that was neither falsy nor 'NONE'
// success, so a bucket whose encryption type Macie never reported rendered a green
// UNKNOWN chip — the one error not to make about an encryption control. Everything
// Macie DID report other than NONE really is encrypted at rest, so that keeps
// success; NONE keeps the alarm colour because it is a measured absence.
const encIntent = (v) => (isEncUnknown(v) ? 'neutral' : v === 'NONE' ? 'warning' : 'success');
const prettyEnc = (v) => (isEncUnknown(v) ? 'UNKNOWN' : v === 'NONE' ? 'None' : v);

const ENC_UNKNOWN_HINT = 'Macie did not report a default encryption type for this bucket. That is not the same as the bucket being encrypted — treat it as unmeasured. Buckets cached before Prism began recording this signal also read UNKNOWN until the next “Refresh buckets” run.';

function EncryptionChip({ value }) {
  const chip = <Chip size="small" label={prettyEnc(value)} sx={statusChipSx(encIntent(value))} />;
  // Only the unknown case carries a tooltip; the measured verdicts say all they
  // need to in the label, and a tooltip on every chip is noise.
  return isEncUnknown(value) ? <Tooltip title={ENC_UNKNOWN_HINT}>{chip}</Tooltip> : chip;
}

// The bucket policy's stance on unencrypted uploads — macie2's
// allowsUnencryptedObjectUploads, carried through to the DTO as
// `allowsUnencryptedUploads` with the enum's own three values.
//
// It rides in the Encryption column because it is the other half of the same
// question: the type chip says how the objects ALREADY in the bucket are
// encrypted, this says whether the policy lets a new object in without
// encryption. A bucket can report AES256 and still accept plaintext uploads, so
// the two are not derivable from each other and the type chip alone overstates
// the posture.
//
// TRUE is the notable case and is the only one drawn in the alarm colour, which
// is the same 'error' triad the Public access column gives PUBLIC.
const UPLOAD_POLICY = {
  TRUE: {
    label: 'Unencrypted uploads allowed',
    intent: 'error',
    hint: 'The bucket policy does not require encryption on upload — an object can be written to this bucket unencrypted, whatever the default encryption type says.',
  },
  FALSE: {
    label: 'Uploads must be encrypted',
    intent: 'success',
    hint: 'The bucket policy rejects an upload that does not specify encryption.',
  },
};

// Anything Macie did not report — the enum's own UNKNOWN, and the empty string a
// row cached before this field was collected still carries — lands here. It is
// deliberately its OWN state and its own neutral colour: "Macie could not tell"
// is not "the policy blocks them", and rendering it as the reassuring case would
// be exactly the wrong error to make about an encryption control.
const UPLOAD_POLICY_UNKNOWN = {
  label: 'Upload policy UNKNOWN',
  intent: 'neutral',
  hint: 'Macie could not determine whether this bucket’s policy requires encryption on upload. This is not the same as the policy refusing unencrypted uploads.',
};

const uploadPolicy = (v) => UPLOAD_POLICY[String(v || '').toUpperCase()] || UPLOAD_POLICY_UNKNOWN;

function UploadPolicyChip({ value }) {
  const p = uploadPolicy(value);
  return (
    <Tooltip title={p.hint}>
      <Chip size="small" label={p.label} sx={statusChipSx(p.intent)} />
    </Tooltip>
  );
}

export default function MacieBuckets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters — seeded from the URL so dashboard posture cards deep-link here.
  const [account, setAccount] = useState(searchParams.get('account') || '');
  const [publicAccess, setPublicAccess] = useState(searchParams.get('public') || '');
  const [shared, setShared] = useState(searchParams.get('shared') || '');
  const [encryption, setEncryption] = useState(searchParams.get('encryption') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    (async () => {
      try {
        const res = await listSecurityHubAccounts();
        setAccounts(res?.data?.accounts || res?.data || []);
      } catch (_) { /* the account filter degrades to id-only labels */ }
    })();
  }, []);

  // Monotonic request guard — the debounce suppresses keystroke storms but does
  // NOT order in-flight responses; without this an earlier request can resolve
  // AFTER the final one and clobber the results (same guard as FindingsList).
  const reqSeq = useRef(0);

  const load = useCallback(async (filters) => {
    const myReq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await listMacieBuckets(filters);
      if (myReq !== reqSeq.current) return;
      setBuckets(res?.data?.buckets || res?.data || []);
      setError(null);
    } catch (e) {
      if (myReq !== reqSeq.current) return;
      setError(e.message);
    } finally {
      if (myReq === reqSeq.current) setLoading(false);
    }
  }, []);

  // Debounced reload + URL sync whenever any filter changes. Rows are cleared
  // up front so the prior query's buckets never linger under the new filters
  // (mirrors FindingsList's setRows([]) on filter change).
  useEffect(() => {
    setBuckets([]);
    const filters = { account, public: publicAccess, shared, encryption, search };
    const t = setTimeout(() => {
      const params = {};
      Object.entries({ account, public: publicAccess, shared, encryption, search }).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      setSearchParams(params, { replace: true });
      load(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [account, publicAccess, shared, encryption, search, load, setSearchParams]);

  // The map holds only REAL names (empty string when Prism has none), so the
  // id-only fallback lives in accountText and cannot be defeated by a map entry
  // that already substituted the id — which is how the duplicated id got printed.
  const accountLabel = useMemo(() => {
    const names = {};
    accounts.forEach((a) => { names[a.accountId] = a.accountName || a.name || ''; });
    return (id) => accountText(names[id], id);
  }, [accounts]);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<FindInPageOutlinedIcon sx={{ fontSize: 24 }} />}
          title="S3 bucket posture"
          subtitle="Public access, encryption, sharing and sensitivity across every enrolled account"
          color={MACIE_COLOR}
        />
      </motion.div>

      {/* === Filters === */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Autocomplete
            size="small"
            options={accounts}
            getOptionLabel={(a) => accountText(a.accountName || a.name, a.accountId)}
            value={accounts.find((a) => a.accountId === account) || null}
            onChange={(_, v) => setAccount(v?.accountId || '')}
            isOptionEqualToValue={(o, v) => o.accountId === v.accountId}
            renderInput={(params) => <TextField {...params} label="Account" placeholder="All accounts" />}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Public access</InputLabel>
            <Select value={publicAccess} label="Public access" onChange={(e) => setPublicAccess(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {PUBLIC_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o.replace(/_/g, ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Shared access</InputLabel>
            <Select value={shared} label="Shared access" onChange={(e) => setShared(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {SHARED_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o.replace(/_/g, ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Encryption</InputLabel>
            <Select value={encryption} label="Encryption" onChange={(e) => setEncryption(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {ENCRYPTION_OPTIONS.map((o) => <MenuItem key={o} value={o}>{prettyEnc(o)}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search buckets"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
        </Stack>
      </Paper>
      </motion.div>

      {/* === Posture table === */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<StorageIcon sx={{ fontSize: 20 }} />}
          title="Buckets"
          subtitle="Every S3 bucket Macie has inventoried for the enrolled accounts"
          color={MACIE_COLOR}
          action={(
            <Chip
              size="small"
              label={`${buckets.length} buckets`}
              sx={statusChipSx('info')}
            />
          )}
        />
        {error && <PortalErrorState error={error} sx={{ mb: 2 }} />}
        {loading && buckets.length === 0 ? (
          <PortalLoading label="Loading bucket posture…" color={MACIE_COLOR} />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Bucket</TableCell>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell sx={headCellSx}>Region</TableCell>
                  <TableCell sx={headCellSx}>Public access</TableCell>
                  <TableCell sx={headCellSx}>Encryption</TableCell>
                  <TableCell sx={headCellSx}>Shared access</TableCell>
                  <TableCell align="right" sx={headCellSx}>Objects</TableCell>
                  <TableCell align="right" sx={headCellSx}>Size</TableCell>
                  <TableCell align="right" sx={headCellSx}>Sensitivity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {buckets.map((b, index) => (
                  <TableRow key={`${b.accountId}/${b.region}/${b.bucketName}`} hover sx={rowSx(index)}>
                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{b.bucketName}</TableCell>
                    <TableCell sx={bodyCellSx}>{accountLabel(b.accountId)}</TableCell>
                    <TableCell sx={bodyCellSx}>{b.region}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip size="small" label={(b.publicAccess || 'UNKNOWN').replace(/_/g, ' ')} sx={statusChipSx(publicIntent(b.publicAccess))} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <EncryptionChip value={b.encryptionType} />
                        <UploadPolicyChip value={b.allowsUnencryptedUploads} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip size="small" label={(b.sharedAccess || 'UNKNOWN').replace(/_/g, ' ')} sx={statusChipSx(sharedIntent(b.sharedAccess))} />
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{(b.objectCount ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{formatBytes(b.sizeBytes)}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {b.sensitivityScore == null || b.sensitivityScore < 0 ? '-' : b.sensitivityScore}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && buckets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<StorageIcon sx={{ fontSize: 28 }} />}
                        title="No buckets match these filters"
                        description="Clear a filter, or run “Refresh buckets” on the dashboard to populate posture."
                        color={MACIE_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      </motion.div>
    </motion.div>
  );
}
